import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
  MessageCircle, MapPin, Calendar, Users, CheckCircle, Star,
  Menu, X, Phone, Facebook, Instagram, ArrowLeft, ShieldCheck,
  CreditCard, Clock, ArrowRight, Waves, Hotel, Sparkles, Send,
  Loader2, Quote, HelpCircle, Palmtree, Anchor, Sun, Utensils,
  Compass, Heart, Bell, Table as TableIcon, ImageIcon, ChevronLeft, ChevronRight
} from 'lucide-react';
 
// ============================================================
// 🌳 شجرة الإعدادات (Logic Center)
// ============================================================
 
const CONFIG = {
  waNumber: '201226292894',       
  phoneDisplay: '01226292894',    
  address: 'مصر — العاشر من رمضان',
  fbLink: 'https://www.facebook.com/Dahab3M',
  logoUrl: 'https://ui-avatars.com/api/?name=3M&background=003B73&color=FFD700&rounded=true&bold=true&size=128',
  fallbackImg: 'https://images.unsplash.com/photo-1507528367768-6c518b01ba90?fm=webp&fit=crop&w=800&q=75',
};
 
// ============================================================
// ⚙️ محرك الـ SEO و GEO الديناميكي
// ============================================================

const SEO_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "TravelAgency",
  "name": "3M Travel",
  "url": "https://www.3mtravel.com",
  "telephone": "01108020868",
  "email": "3mgs.eg@gmail.com",
  "address": {
    "@type": "PostalAddress",
    "addressLocality": "العاشر من رمضان",
    "addressCountry": "EG"
  }
};

const generateDynamicSchema = (destData) => {
  if (!destData) return SEO_SCHEMA;
  return {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "TravelAgency",
        "name": "3M Travel",
        "url": `https://www.3mtravel.com/#/${destData.id}`,
        "description": destData.desc
      }
    ]
  };
};

// ============================================================
// 🌳 شجرة البيانات الكاملة (Restored Content)
// ============================================================
 
const TRAVEL_DATA = [
  {
    id: 'sharm-el-sheikh-trip',
    name: 'شرم الشيخ',
    mainImg: 'https://images.unsplash.com/photo-1539367628448-4bc5c9d171c8?fm=webp&fit=crop&w=800&q=75',
    desc: 'دليلك الشامل لرحلات شرم الشيخ 2026. اكتشف خدمات حجز اليخوت من فندق مارينا شرم وأفضل أنشطة الغوص في محمية رأس محمد.',
    cityFeatures: [
      { icon: Anchor,   text: 'أفضل مناطق الغوص والسنوركلينج عالمياً' },
      { icon: Sun,      text: 'أجواء مشمسة دافئة طوال العام' },
      { icon: Utensils, text: 'تنوع المطاعم العالمية في خليج نعمة' },
    ],
    packages: [
      {
        id: 'p1',
        title: 'عرض فندق مارينا شرم',
        hotel: 'مارينا شرم (خليج نعمة)',
        price: '11,700',
        duration: '4 أيام / 3 ليالي',
        category: 'عرض خاص',
        tag: 'متبقي 3 أماكن فقط!',
        features: ['إقامة فندقية فاخرة', 'إطالة مباشرة على البحر', 'نصف إقامة (فطار + عشاء)', 'انتقالات ذهاب وعودة'],
        fullDesc: 'استمتع بإقامة فاخرة في قلب خليج نعمة. يتميز فندق مارينا شرم بإطلالات بانورامية ساحرة على البحر الأحمر، شواطئ رملية خاصة، ومسابح متعددة تناسب جميع أفراد العائلة، بالإضافة لبرنامج ترفيهي مسائي مميز.',
        gallery: [
          'https://images.unsplash.com/photo-1582719508461-905c673771fd?fm=webp&w=800&q=75',
          'https://images.unsplash.com/photo-1566073771259-6a8506099945?fm=webp&w=800&q=75',
          'https://images.unsplash.com/photo-1618773928121-c32242e63f39?fm=webp&w=800&q=75'
        ],
        hotelFaq: [
          { q: 'ما هي مميزات الحجز في فنادق مارينا شرم المطلة مباشرة على خليج نعمة؟', a: 'الحجز عبر 3M Travel يضمن لك غرفاً حصرية مطلة على البحر وأولوية في التسكين، بفضل شراكاتنا المباشرة مع إدارة الفنادق في منطقة المارينا.' },
          { q: 'لماذا يعتبر الحجز عبر وكالة سياحة لعروض مارينا شرم أفضل من منصات الحجز العالمية؟', a: 'نوفر أسعاراً محلية خاصة غير متاحة على المنصات العالمية، بالإضافة إلى دعم مباشر ومرونة في التعديل والإلغاء لضمان راحة العميل.' }
        ]
      },
      {
        id: 'p2',
        title: 'رحلات شرم الشيخ VIP',
        hotel: 'فنادق 5 نجوم ديلوكس',
        price: '12,500',
        duration: '5 أيام / 4 ليالي',
        category: 'VIP',
        tag: 'الأكثر مبيعاً',
        features: ['سيارة خاصة', 'مرشد سياحي', 'يخت خاص', 'إقامة كاملة'],
        fullDesc: 'تجربة استثنائية متكاملة في شرم الشيخ. إقامة في فنادق 5 نجوم ديلوكس، شاملة رحلات يخت خاصة، وانتقالات بسيارات فاخرة، مع مرشد سياحي مخصص لضمان راحة ورفاهية مطلقة.',
        gallery: [
          'https://images.unsplash.com/photo-1544551763-46a013bb70d5?fm=webp&w=800&q=75',
          'https://images.unsplash.com/photo-1590490359683-658d3d23f972?fm=webp&w=800&q=75'
        ]
      }
    ],
    geoData: {
      priceTable: [
        { activity: 'رحلة يخت محمية رأس محمد', price: 'طلب سعر', type: 'شامل الغداء' },
        { activity: 'حجز يخت خاص من المارينا', price: 'طلب سعر', type: 'نصف يوم' },
      ],
      faq: [
        { q: 'كيف أخطط لرحلة سياحية متكاملة إلى شرم الشيخ بميزانية متوسطة للعائلة؟', a: 'توفر 3M Travel خطط إجازات عائلية مدروسة التكلفة لشرم الشيخ، تشمل حجز فنادق 4 نجوم مع أكوا بارك وتأمين انتقالات مريحة، مما يضمن أعلى قيمة مقابل السعر.' },
        { q: 'ما هي أفضل عروض شركات السياحة التي توفر برامج شرم الشيخ شاملة الانتقالات والوجبات؟', a: 'تتميز 3M Travel بتقديم باقات إقامة شاملة (All-Inclusive) في شرم الشيخ، تغطي الإقامة والوجبات والانتقالات بأحدث الباصات السياحية المجهزة.' },
      ],
    },
  },
  {
    id: 'hurghada-trip',
    name: 'الغردقة',
    mainImg: 'https://images.unsplash.com/photo-1600289031464-74d374b64991?fm=webp&fit=crop&w=800&q=75',
    desc: 'عروض الغردقة 2026: رحلات جزيرة الجفتون وألعاب الأكوا بارك العالمية.',
    cityFeatures: [
      { icon: Waves,  text: 'أكبر مدن الألعاب المائية' },
      { icon: Anchor, text: 'رحلات الجزر البحرية' },
    ],
    packages: [
      {
        id: 'p3',
        title: 'رحلات الغردقة عائلية',
        hotel: 'فنادق 5 نجوم (أكوا بارك)',
        price: '6,900',
        duration: '4 أيام / 3 ليالي',
        category: 'عائلي',
        tag: 'عائلي مميز',
        features: ['أكوا بارك مجاني', 'إقامة كاملة', 'رحلة سفاري', 'نادي للأطفال'],
        fullDesc: 'باقة العائلة المثالية في الغردقة! استمتعوا بإقامة في أضخم منتجعات الأكوا بارك، مع وصول لا محدود للألعاب المائية، ونادي ترفيهي للأطفال، بالإضافة لرحلة سفاري ممتعة في الصحراء تناسب جميع الأعمار.',
        gallery: [
          'https://images.unsplash.com/photo-1582653291997-079a1c04e5d1?fm=webp&w=800&q=75',
          'https://images.unsplash.com/photo-1533050487297-09b450131914?fm=webp&w=800&q=75'
        ]
      },
    ],
    geoData: {
      priceTable: [{ activity: 'جزيرة الجفتون', price: 'طلب سعر', type: 'يوم كامل' }],
      faq: [
        { q: 'ما هي أفضل منتجعات الغردقة؟', a: 'نتعاقد مع أفضل فنادق الأكوا بارك لضمان تجربة عائلية ممتعة.' },
      ],
    },
  },
  {
    id: 'dahab-trip',
    name: 'دهب',
    mainImg: 'https://images.unsplash.com/photo-1519046904884-53103b34b206?fm=webp&fit=crop&w=800&q=75',
    desc: 'مغامرات دهب 2026: البلو هول، سفاري الجبال، وأجواء بدوية.',
    cityFeatures: [{ icon: Compass, text: 'مغامرات جبلية وبحرية' }],
    packages: [
      {
        id: 'p6',
        title: 'رحلات دهب مغامرات',
        hotel: 'كامبات وفنادق مميزة',
        price: '3,500',
        duration: '3 أيام',
        category: 'شبابي',
        tag: 'شبابي نشط',
        features: ['البلو هول', 'سفاري الوشواش', 'سهرة بدوية'],
        fullDesc: 'المغامرة تناديك! استكشف أعماق البلو هول، وعِش أجواء البادية في سهرة جبلية ساحرة، مع رحلة سفاري مثيرة إلى وادي الوشواش. إقامة مريحة في أفضل مخيمات وفنادق دهب ذات الطابع الأصيل.',
        gallery: [
          'https://images.unsplash.com/photo-1605281317010-fe5ffe798166?fm=webp&w=800&q=75',
          'https://images.unsplash.com/photo-1519046904884-53103b34b206?fm=webp&w=800&q=75'
        ]
      },
    ],
    geoData: {
      priceTable: [{ activity: 'رحلة البلو هول', price: 'طلب سعر', type: 'يوم كامل' }],
      faq: [
        { q: 'هل الرحلات مناسبة للمجموعات؟', a: 'نعم، نوفر باقات خاصة للمجموعات الشبابية بأسعار تنافسية.' },
      ],
    },
  },
  {
    id: 'matrouh-trip',
    name: 'مرسى مطروح',
    mainImg: 'https://images.unsplash.com/photo-1510414842594-a61c69b5ae57?fm=webp&fit=crop&w=800&q=75',
    desc: 'صيف مطروح 2026: مياه عجيبة الصافية ورمال الغرام الناعمة بأسعار عائلية.',
    cityFeatures: [{ icon: Waves, text: 'مياه فيروزية ساحرة' }],
    packages: [
      {
        id: 'p7',
        title: 'مطروح صيف 2026',
        hotel: 'أبراج فندقية مطلة',
        price: '5,800',
        duration: '5 أيام',
        category: 'عائلي',
        tag: 'صيفي',
        features: ['شاطئ عجيبة', 'سوق ليبيا', 'إقامة مريحة'],
        fullDesc: 'صيف مطروح لا يُعوض. شقق وأبراج فندقية مريحة ومجهزة بالكامل لعائلتك، مع إطلالات ساحرة، وقرب من أشهر الشواطئ مثل عجيبة والغرام، للاستمتاع برمال ناعمة ومياه تركوازية صافية.',
        gallery: [
          'https://images.unsplash.com/photo-1510414842594-a61c69b5ae57?fm=webp&w=800&q=75',
          'https://images.unsplash.com/photo-1522708323590-d24dbb6b0267?fm=webp&w=800&q=75'
        ]
      },
    ],
    geoData: {
      priceTable: [{ activity: 'إيجار شقق فندقية', price: 'طلب سعر', type: 'يومي' }],
      faq: [
        { q: 'هل تتوفر شقق فندقية؟', a: 'نعم، نوفر أرقى الشقق المطلة على البحر مباشرة.' },
      ],
    },
  },
  {
    id: 'marsa-alam-trip',
    name: 'مرسى علم',
    mainImg: 'https://images.unsplash.com/photo-1544551763-46a013bb70d5?fm=webp&fit=crop&w=800&q=75',
    desc: 'استكشف الطبيعة البكر في مرسى علم: الغوص مع الدلافين وشواطئ فيروزية.',
    cityFeatures: [{ icon: Compass, text: 'مشاهدة الدلافين والسلاحف' }],
    packages: [
      {
        id: 'p4',
        title: 'استجمام مرسى علم',
        hotel: 'فنادق 5 نجوم لاكشري',
        price: '9,500',
        duration: '5 أيام',
        category: 'VIP',
        tag: 'استجمام',
        features: ['بيت الدلافين', 'شاطئ النيزك', 'إقامة هادئة'],
        fullDesc: 'ملاذك الهادئ بعيداً عن صخب المدينة. إقامة فاخرة في منتجعات مرسى علم، مع رحلة بحرية حصرية لبيت الدلافين، وزيارة لشاطئ النيزك الساحر للاستمتاع بالطبيعة البكر والهدوء التام.',
        gallery: [
          'https://images.unsplash.com/photo-1544550581-5f7ceaf7f992?fm=webp&w=800&q=75',
          'https://images.unsplash.com/photo-1499793983690-e29da59ef1c2?fm=webp&w=800&q=75'
        ]
      },
    ],
    geoData: {
      priceTable: [{ activity: 'بيت الدلافين', price: 'طلب سعر', type: 'رحلة بحرية' }],
      faq: [{ q: 'متى أفضل وقت للزيارة؟', a: 'طوال العام، خاصة لمحبي الغوص.' }],
    },
  },
];

const REVIEWS_DATA = [
  { id: 1, user: 'م. أحمد سالم', rating: 5, comment: 'الالتزام بالمواعيد والأسعار كان دقيقاً جداً. فندق مارينا شرم كان اختياراً رائعاً.', result: 'دقة المواعيد' },
  { id: 2, user: 'سارة محمود', rating: 5, comment: 'خدمة العملاء سريعة جداً عبر واتساب. شكراً 3M ترافيل لتنسيق رحلتنا.', result: 'سرعة الاستجابة' },
  { id: 3, user: 'د. طارق زيدان', rating: 5, comment: 'باقة شهر العسل كانت منظمة باحترافية عالية، استمتعنا بكل لحظة.', result: 'جودة التنظيم' },
];

const FAKE_BOOKINGS = [
  { name: 'أحمد',    city: 'القاهرة',      pkg: 'فندق مارينا شرم',       time: 'ساعتين' },
  { name: 'ملك',    city: 'الإسكندرية',   pkg: 'رحلات الغردقة عائلية',   time: '15 دقيقة' },
  { name: 'محمود',   city: 'المنصورة',     pkg: 'رحلات دهب مغامرات',      time: 'ساعة' },
  { name: 'د. يوسف', city: 'الجيزة',       pkg: 'باقة شهر العسل',         time: '3 ساعات' },
  { name: 'منى',     city: 'الساحل',       pkg: 'مطروح صيف 2026',         time: 'نصف ساعة' },
];
 
// ============================================================
// ─── مكونات المساعدة (Helpers) ──────────────────────────────
// ============================================================
 
const getWaLink = (msg) => `https://wa.me/${CONFIG.waNumber}?text=${encodeURIComponent(msg)}`;
 
const SafeImage = ({ src, alt, className, priority, ...rest }) => {
  const [imgSrc, setImgSrc] = useState(src || CONFIG.fallbackImg);
  useEffect(() => setImgSrc(src || CONFIG.fallbackImg), [src]);
  return (
    <img
      src={imgSrc}
      alt={alt}
      className={className}
      loading={priority ? "eager" : "lazy"}
      fetchpriority={priority ? "high" : "auto"}
      onError={() => setImgSrc(CONFIG.fallbackImg)}
      {...rest}
    />
  );
};

// ============================================================
// 🏗️ مكون النافذة المنبثقة للتفاصيل (Package Modal)
// ============================================================
const PackageDetailsModal = ({ pkg, onClose }) => {
  const galleryRef = useRef(null);

  const scrollGallery = (direction) => {
    if (galleryRef.current) {
      const scrollAmount = direction === 'next' ? -galleryRef.current.offsetWidth : galleryRef.current.offsetWidth;
      galleryRef.current.scrollBy({ left: scrollAmount, behavior: 'smooth' });
    }
  };

  if (!pkg) return null;

  return (
    <div className="fixed inset-0 z-[600] flex items-center justify-center bg-[#003B73]/95 backdrop-blur-md p-2 md:p-6 animate-fade-in overflow-hidden">
      <div className="bg-gray-50 rounded-[2.5rem] w-full max-w-5xl max-h-[92vh] flex flex-col shadow-2xl relative animate-slide-up overflow-hidden border border-white/20">
        
        <div className="absolute top-6 left-6 right-6 flex justify-between items-center z-[700] pointer-events-none">
          <button 
            onClick={onClose} 
            className="pointer-events-auto bg-white/95 backdrop-blur-md px-6 py-3 rounded-full text-[#003B73] hover:bg-white transition-all shadow-xl flex items-center gap-2 text-sm font-black border border-blue-100/50 active:scale-95"
          >
            <ArrowRight size={20} className="text-[#FFD700]" /> العودة للوجهة
          </button>
          <button 
            onClick={onClose} 
            className="pointer-events-auto bg-black/50 backdrop-blur-md p-3 rounded-full text-white hover:bg-red-500 transition-all shadow-xl border border-white/20 active:scale-90"
          >
            <X size={24} />
          </button>
        </div>

        <div className="overflow-y-auto w-full h-full flex flex-col relative hide-scrollbar pb-28">
          <div className="relative w-full h-[45vh] min-h-[300px] shrink-0 group">
            <div ref={galleryRef} className="flex overflow-x-auto snap-x snap-mandatory hide-scrollbar h-full w-full scroll-smooth">
              {pkg.gallery?.map((img, idx) => (
                <div key={idx} className="w-full h-full shrink-0 snap-center relative">
                  <SafeImage src={img} className="w-full h-full object-cover" alt={`${pkg.title} - ${idx + 1}`} />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/30 to-transparent flex items-end p-10 z-10">
                     <div className="w-full">
                        <div className="bg-[#FFD700] text-[#003B73] px-5 py-2 rounded-full font-black text-xs md:text-sm mb-4 inline-block shadow-lg border border-white/20 animate-pulse">{pkg.tag}</div>
                        <h2 className="text-4xl md:text-5xl font-black text-white drop-shadow-2xl tracking-tight mb-4">{pkg.title}</h2>
                        <div className="flex gap-2 mt-4">
                           {pkg.gallery.map((_, dotIdx) => (
                              <div key={dotIdx} className={`h-2 rounded-full transition-all duration-300 ${idx === dotIdx ? 'w-10 bg-[#FFD700]' : 'w-3 bg-white/40'}`} />
                           ))}
                        </div>
                     </div>
                  </div>
                </div>
              ))}
            </div>

            {pkg.gallery?.length > 1 && (
               <div className="absolute top-1/2 -translate-y-1/2 w-full px-6 flex justify-between z-20 pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity">
                 <button onClick={() => scrollGallery('prev')} className="pointer-events-auto bg-white/30 backdrop-blur-md p-4 rounded-full text-white hover:bg-[#FFD700] transition-all shadow-2xl border border-white/30"><ChevronRight size={28}/></button>
                 <button onClick={() => scrollGallery('next')} className="pointer-events-auto bg-white/30 backdrop-blur-md p-4 rounded-full text-white hover:bg-[#FFD700] transition-all shadow-2xl border border-white/30"><ChevronLeft size={28}/></button>
               </div>
            )}
          </div>

          <div className="p-8 md:p-14 shrink-0 bg-white relative z-30 -mt-10 rounded-t-[3rem] shadow-2xl">
            <div className="flex flex-wrap gap-4 mb-10 text-sm md:text-base font-bold text-[#003B73]">
              <div className="bg-blue-50/50 px-6 py-4 rounded-2xl flex items-center gap-3 border border-blue-100 shadow-sm"><Hotel size={24} className="text-[#FFD700]"/> {pkg.hotel}</div>
              <div className="bg-blue-50/50 px-6 py-4 rounded-2xl flex items-center gap-3 border border-blue-100 shadow-sm"><Calendar size={24} className="text-[#FFD700]"/> {pkg.duration}</div>
              <div className="bg-green-50 text-green-700 px-6 py-4 rounded-2xl flex items-center gap-3 border border-green-100 shadow-sm"><CreditCard size={24} className="text-green-600"/> {pkg.price} {pkg.price !== 'حسب الطلب' && 'ج.م'}</div>
            </div>
            
            {pkg.fullDesc && (
              <div className="mb-12">
                <h3 className="font-black text-2xl mb-5 text-[#003B73] border-r-[6px] border-[#FFD700] pr-4">تفاصيل الإقامة</h3>
                <p className="text-gray-600 font-bold leading-relaxed text-lg md:text-xl">{pkg.fullDesc}</p>
              </div>
            )}
            
            <div className="mb-12">
              <h3 className="font-black text-2xl mb-6 text-[#003B73] border-r-[6px] border-[#FFD700] pr-4">ماذا يشمل العرض؟</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                {pkg.features.map((feat, i) => (
                  <div key={i} className="flex items-center gap-4 text-lg text-gray-700 font-bold bg-gray-50 p-5 rounded-2xl border border-gray-100 hover:border-blue-200 transition-colors">
                    <CheckCircle size={24} className="text-green-500 shrink-0"/> {feat}
                  </div>
                ))}
              </div>
            </div>

            {pkg.hotelFaq?.length > 0 && (
              <div className="mb-12">
                <h3 className="font-black text-2xl mb-6 text-[#003B73] border-r-[6px] border-[#FFD700] pr-4">أسئلة شائعة عن هذا الفندق</h3>
                <div className="space-y-4">
                  {pkg.hotelFaq.map((faq, i) => (
                    <details key={i} className="group bg-blue-50/30 rounded-2xl border border-blue-100/50 overflow-hidden cursor-pointer shadow-sm transition-all hover:shadow-md">
                      <summary className="p-6 font-bold text-[#003B73] flex justify-between items-center outline-none list-none text-lg cursor-pointer">
                        {faq.q}
                        <ChevronLeft size={22} className="text-[#FFD700] shrink-0 transition-transform group-open:-rotate-90" />
                      </summary>
                      <div className="p-6 pt-0 text-gray-600 font-bold leading-relaxed border-t border-blue-100/30">
                        {faq.a}
                      </div>
                    </details>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="absolute bottom-0 left-0 w-full bg-white/95 backdrop-blur-md border-t border-gray-100 p-6 z-[700] shadow-[0_-15px_40px_rgba(0,0,0,0.1)]">
            <div className="max-w-4xl mx-auto flex flex-col md:flex-row items-center gap-6">
               <div className="hidden md:block flex-1">
                  <p className="text-[#003B73] font-black text-lg">جاهز للانطلاق؟</p>
                  <p className="text-gray-500 font-bold text-sm italic">تواصل معنا الآن لتأكيد توافر الغرف.</p>
               </div>
               <a 
                href={getWaLink(`مرحباً 3M Travel، أرغب في حجز وتأكيد [ ${pkg.title} ] بمدينة ${pkg.cityName || 'الوجهة'}. برجاء المتابعة معي.`)} 
                target="_blank" 
                rel="noopener noreferrer" 
                className="w-full md:w-auto md:min-w-[400px] bg-[#003B73] text-white py-5 rounded-2xl font-black text-xl shadow-2xl hover:bg-[#FFD700] hover:text-[#003B73] transition-all flex items-center justify-center gap-4 active:scale-95"
               >
                 تأكيد الحجز عبر واتساب <MessageCircle size={30} />
               </a>
            </div>
        </div>

      </div>
    </div>
  );
};
 
// ============================================================
// 🤖 مستشار الرحلات الذكي (AI Planner)
// ============================================================
 
const AITravelPlanner = ({ destination }) => {
  const [status, setStatus] = useState('idle');
  const [itinerary, setItinerary] = useState('');
 
  const generate = async () => {
    setStatus('loading');
    try {
      const apiKey = ""; 
      const url = `https://generativelanguage.googleapis.com/v1beta/models/gemini-2.5-flash-preview-09-2025:generateContent?key=${apiKey}`;
      const payload = {
        contents: [{ parts: [{ text: `خطط لرحلة إلى ${destination}.` }] }],
        systemInstruction: { parts: [{ text: 'أنت خبير سياحي في 3M Travel. صمم جدولاً منطقياً لمدة 4 أيام للوجهة المذكورة. ابدأ بالجدول مباشرة.' }] }
      };

      const res = await fetch(url, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload) });
      const data = await res.json();
      setItinerary(data.candidates?.[0]?.content?.parts?.[0]?.text || 'لم نتمكن من جلب الخطة حالياً.');
      setStatus('done');
    } catch {
      setStatus('error');
    }
  };
 
  return (
    <div className="bg-gradient-to-br from-[#003B73] to-[#00264D] p-8 rounded-[2.5rem] text-white shadow-2xl mb-10 border border-blue-800 relative overflow-hidden">
      <div className="flex items-center gap-3 mb-6 relative z-10">
        <Sparkles className="text-[#FFD700] animate-pulse" size={28} />
        <h3 className="text-2xl font-black">مستشار {destination} الذكي ✨</h3>
      </div>
      {status === 'idle' && (
        <button onClick={generate} className="w-full bg-[#FFD700] text-[#003B73] py-4 rounded-xl font-black text-lg hover:bg-white transition-all active:scale-95">
          خطط لرحلتي بذكاء
        </button>
      )}
      {status === 'loading' && (
        <div className="flex flex-col items-center py-6">
          <Loader2 className="animate-spin mb-4 text-[#FFD700]" size={40} />
          <p className="font-bold">جاري تصميم جدولك...</p>
        </div>
      )}
      {(status === 'done' || status === 'error') && (
        <div className="bg-white/10 backdrop-blur-md p-6 rounded-2xl border border-white/10">
          <p className="text-blue-50 text-sm whitespace-pre-wrap leading-relaxed font-bold">{itinerary || 'عذراً، حاول مرة أخرى.'}</p>
          <button onClick={() => setStatus('idle')} className="mt-4 text-xs font-black text-[#FFD700] underline">إعادة التخطيط</button>
        </div>
      )}
    </div>
  );
};

// ============================================================
// 🏗️ مكون الباقات (Packages Section)
// ============================================================
const PackagesSection = ({ onSelectPackage }) => {
  const [filter, setFilter] = useState('الكل');
  
  const allPackages = useMemo(() => {
    return TRAVEL_DATA.flatMap(city => 
      city.packages.map(pkg => ({ ...pkg, cityName: city.name }))
    );
  }, []);

  const filtered = useMemo(() => {
    if (filter === 'الكل') return allPackages;
    return allPackages.filter(p => p.category === filter || (p.tag && p.tag.includes(filter)));
  }, [filter, allPackages]);

  const FILTER_OPTIONS = ['الكل', 'الأكثر مبيعاً', 'عائلي', 'شبابي', 'VIP', 'عرض خاص'];

  return (
    <section id="packages" className="py-24 bg-gray-50">
      <div className="container mx-auto px-6">
        <div className="text-center mb-14">
          <h2 className="text-4xl md:text-5xl font-black text-[#003B73] mb-4 tracking-tighter">عروض الباقات الحالية</h2>
          <p className="text-gray-500 font-bold mb-6 italic text-lg">تصفح أقوى العروض الحصرية المختارة لك بعناية</p>
          <div className="w-20 h-1.5 bg-[#FFD700] mx-auto rounded-full" />
        </div>
        
        <div className="flex flex-wrap justify-center gap-3 mb-16">
          {FILTER_OPTIONS.map((f) => (
            <button 
              key={f} 
              onClick={() => setFilter(f)} 
              className={`px-8 py-3 rounded-full font-bold text-sm transition-all shadow-sm border ${filter === f ? 'bg-[#003B73] text-white border-[#003B73] scale-105' : 'bg-white text-gray-600 border-gray-200 hover:border-[#003B73]'}`}
            >
              {f}
            </button>
          ))}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10">
          {filtered.map((pkg) => (
            <div key={pkg.id} className="bg-white rounded-[2.5rem] shadow-lg hover:shadow-2xl border border-gray-100 overflow-hidden flex flex-col hover:-translate-y-2 transition-all duration-300 group">
              <div className="bg-[#FFD700] text-[#003B73] py-2 px-6 text-center text-xs font-black uppercase tracking-widest">{pkg.tag}</div>
              <div className="p-8 flex flex-col flex-1">
                <div className="text-center mb-8 border-b border-gray-100 pb-6">
                  <h3 className="text-2xl font-black text-[#003B73] mb-3 group-hover:text-[#FFD700] transition-colors">{pkg.title}</h3>
                  <div className="text-gray-500 font-bold mb-3 flex items-center justify-center gap-2">
                    <Calendar size={18} className="text-[#FFD700]" /> {pkg.duration}
                  </div>
                  <div className="text-[#003B73] font-bold flex items-center justify-center gap-2">
                    <Hotel size={20} className="text-[#FFD700]" /> {pkg.hotel}
                  </div>
                </div>
                <ul className="space-y-4 mb-8 flex-1">
                  {pkg.features.map((feat, i) => (
                    <li key={i} className="flex items-start gap-3 text-sm font-bold text-gray-700 bg-gray-50/50 p-3 rounded-xl border border-gray-50">
                      <CheckCircle size={18} className="text-green-500 shrink-0 mt-0.5" /> {feat}
                    </li>
                  ))}
                </ul>
                <button
                  onClick={() => onSelectPackage(pkg)}
                  className="w-full bg-[#003B73] text-white py-4 rounded-xl font-black shadow-md hover:bg-[#FFD700] hover:text-[#003B73] transition-all flex items-center justify-center gap-3 group/btn mt-auto active:scale-95"
                >
                  عرض التفاصيل والصور <ImageIcon size={20} className="group-hover/btn:scale-110 transition-transform" />
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};
 
// ============================================================
// صفحة الوجهة التفصيلية (Destination Page)
// ============================================================
 
const DestinationPage = ({ data, goBack, onSelectPackage }) => (
  <article dir="rtl" className="bg-gray-50 min-h-screen pt-32 pb-20">
    <div className="container mx-auto px-6">
      <button onClick={goBack} className="bg-white text-[#003B73] border border-gray-200 px-7 py-3 rounded-full font-black mb-10 flex items-center gap-3 shadow-md hover:bg-gray-100 transition-all active:scale-95">
        <ArrowRight size={22} className="text-[#FFD700]" /> العودة للرئيسية
      </button>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
        <section className="animate-fade-in">
          <h1 className="text-6xl md:text-7xl font-black text-[#003B73] mb-8 tracking-tighter leading-tight drop-shadow-sm">{data.name}</h1>
          <p className="text-xl text-gray-600 font-bold leading-relaxed mb-12 border-r-4 border-[#FFD700] pr-6 italic">{data.desc}</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 mb-14">
            {data.cityFeatures?.map((f, i) => (
              <div key={i} className="bg-white p-6 rounded-2xl border border-gray-100 flex items-center gap-4 font-black text-[#003B73] shadow-sm hover:shadow-md transition-shadow">
                <div className="bg-blue-50 p-3 rounded-xl"><f.icon size={26} className="text-[#FFD700] shrink-0" /></div> {f.text}
              </div>
            ))}
          </div>
          
          <h2 className="text-4xl font-black text-[#003B73] mb-10 border-r-8 border-[#FFD700] pr-5">أقوى العروض في {data.name}</h2>
          <div className="space-y-8 mb-20">
            {data.packages?.map((pkg) => (
              <div key={pkg.id} className="bg-white rounded-[2.5rem] p-8 md:p-10 border border-gray-100 shadow-xl hover:shadow-2xl transition-all relative overflow-hidden group">
                <div className="absolute top-0 left-0 bg-[#FFD700] text-[#003B73] px-8 py-2 text-sm font-black shadow-md">{pkg.tag}</div>
                <div className="flex flex-col xl:row justify-between items-start xl:items-center gap-10 mt-6 xl:mt-0">
                  <div className="flex-1">
                    <h3 className="text-3xl font-black text-[#003B73] mb-4 group-hover:text-[#FFD700] transition-colors">{pkg.title}</h3>
                    <div className="flex items-center gap-3 text-gray-500 font-bold mb-6 bg-gray-50 p-3 rounded-xl w-max"><Hotel size={20} className="text-[#FFD700]" /> {pkg.hotel}</div>
                    <ul className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      {pkg.features.map((feat, i) => (
                        <li key={i} className="flex items-start gap-3 text-base text-gray-600 font-bold"><CheckCircle size={20} className="text-green-500 shrink-0 mt-0.5" /> {feat}</li>
                      ))}
                    </ul>
                  </div>
                  <div className="text-right w-full xl:w-auto bg-blue-50/50 p-8 rounded-3xl border border-blue-100 shrink-0 shadow-inner">
                    <div className="text-sm text-gray-500 font-black mb-2">السعر التقديري للفرد</div>
                    <div className="text-4xl font-black text-[#003B73] mb-6 tracking-tighter">{pkg.price} <span className="text-xl">{pkg.price !== 'حسب الطلب' && 'ج.م'}</span></div>
                    <button
                      onClick={() => onSelectPackage({ ...pkg, cityName: data.name })}
                      className="w-full bg-[#003B73] text-white px-10 py-4 rounded-2xl font-black text-lg shadow-lg hover:bg-[#FFD700] hover:text-[#003B73] transition-all flex items-center justify-center gap-3 active:scale-95"
                    >
                      التفاصيل والصور <ImageIcon size={22} />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <section className="faq-ui mb-10">
            <h2 className="text-3xl font-black text-[#003B73] mb-8 flex items-center gap-3"><HelpCircle className="text-[#FFD700]" size={32} /> دليل الاستفسارات حول {data.name}</h2>
            <div className="space-y-5">
              {data.geoData?.faq?.map((item, i) => (
                <div key={i} className="bg-white p-8 rounded-3xl border border-gray-100 shadow-md hover:shadow-lg transition-shadow">
                  <h4 className="font-black text-xl text-[#003B73] mb-4 leading-relaxed border-r-4 border-blue-100 pr-4">س: {item.q}</h4>
                  <p className="text-gray-600 font-bold leading-relaxed text-lg italic">ج: {item.a}</p>
                </div>
              ))}
            </div>
          </section>
        </section>

        <aside className="space-y-10">
          <AITravelPlanner destination={data.name} />
          <div className="bg-white p-10 rounded-[3rem] border border-gray-100 shadow-2xl sticky top-28 overflow-hidden group">
            <h3 className="text-2xl font-black text-[#003B73] mb-8 flex items-center gap-3 relative z-10"><TableIcon className="text-[#FFD700] shrink-0" size={28} /> قائمة الأنشطة المتاحة</h3>
            <div className="space-y-4 relative z-10">
              {data.geoData?.priceTable?.map((row, i) => (
                <div key={i} className="flex justify-between items-center bg-gray-50/50 p-5 rounded-2xl font-black border border-gray-100 hover:bg-white transition-colors">
                  <span className="text-gray-700 text-base">{row.activity}</span>
                  {row.price === 'طلب سعر' ? (
                     <a 
                       href={getWaLink(`مرحباً 3M Travel، أرغب في الاستفسار عن تفاصيل وسعر النشاط: [ ${row.activity} ] في مدينة ${data.name}.`)}
                       target="_blank" 
                       rel="noopener noreferrer"
                       className="bg-[#25D366] text-white px-5 py-2.5 rounded-xl text-sm font-black hover:bg-green-600 transition-all flex items-center gap-2 shadow-md active:scale-90"
                     >
                       <MessageCircle size={18}/> طلب السعر
                     </a>
                  ) : (
                     <span className="text-green-600 font-black text-lg">{row.price}</span>
                  )}
                </div>
              ))}
            </div>
            <p className="text-sm font-bold text-gray-400 leading-relaxed text-center mt-8 p-5 bg-blue-50/20 rounded-2xl border border-blue-50 relative z-10">
              ملاحظة: الأسعار تقديرية وتتغير حسب توقيت الحجز والموسم السياحي.
            </p>
          </div>
        </aside>
      </div>
    </div>
  </article>
);
 
// ============================================================
// تطبيق 3M Travel الرئيسي (App Entry Point)
// ============================================================
 
export default function App() {
  const [route, setRoute] = useState('/');
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [toastData, setToastData] = useState(null);
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [form, setForm] = useState({ name: '', phone: '', destination: '', travelers: '', date: '' });
  const [submittedData, setSubmittedData] = useState(null); 

  useEffect(() => {
    const onScroll = () => setIsScrolled(window.scrollY > 20);
    window.addEventListener('scroll', onScroll, { passive: true });
 
    const onHash = () => {
      const h = window.location.hash;
      setRoute(h.startsWith('#/') ? h.replace('#/', '') : '/');
    };
    window.addEventListener('hashchange', onHash);
    onHash();
 
    const toastTimer = setInterval(() => {
      const rand = FAKE_BOOKINGS[Math.floor(Math.random() * FAKE_BOOKINGS.length)];
      setToastData(rand);
      setTimeout(() => setToastData(null), 6000);
    }, 25000);
 
    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('hashchange', onHash);
      clearInterval(toastTimer);
    };
  }, []);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    const destData = TRAVEL_DATA.find((d) => d.id === route);
    if (destData) {
      document.title = `عروض رحلات ${destData.name} 2026 | أفضل الفنادق والباقات | 3M Travel`;
    } else {
      document.title = "3M Travel | وكالة السياحة الداخلية الرائدة في مصر";
    }
  }, [route]);

  const destData = useMemo(() => TRAVEL_DATA.find((d) => d.id === route), [route]);
  const dynamicSchema = useMemo(() => generateDynamicSchema(destData), [destData]);

  const handleFormChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });
  
  const handleFormSubmit = (e) => {
    e.preventDefault();
    setSubmittedData({ ...form });
    setShowSuccessModal(true);
    setForm({ name: '', phone: '', destination: '', travelers: '', date: '' });
  };
 
  return (
    <div dir="rtl" className="font-sans antialiased bg-white text-gray-900" style={{ fontFamily: "'Cairo', sans-serif" }}>
      <script src="https://cdn.tailwindcss.com"></script>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(dynamicSchema) }} />
      <link href="https://fonts.googleapis.com/css2?family=Cairo:wght@400;600;700;900&display=swap" rel="stylesheet" />
 
      <PackageDetailsModal pkg={selectedPackage} onClose={() => setSelectedPackage(null)} />

      {showSuccessModal && (
        <div className="fixed inset-0 z-[700] flex items-center justify-center bg-[#003B73]/90 backdrop-blur-xl p-4 animate-fade-in">
          <div className="bg-white rounded-[3rem] p-10 max-w-xl w-full text-center shadow-3xl border-b-[10px] border-[#FFD700]">
            <div className="w-24 h-24 bg-green-50 rounded-full flex items-center justify-center mx-auto mb-8 animate-bounce"><CheckCircle size={50} className="text-green-500" /></div>
            <h3 className="text-3xl font-black text-[#003B73] mb-4">وصلنا طلبك!</h3>
            <p className="text-gray-600 font-bold text-lg mb-10 leading-relaxed italic">خطوة واحدة تفصلك عن عطلة العمر ✨</p>
            <a
              href={getWaLink(`مرحباً 3M Travel، أرغب في تأكيد حجز رحلتي.\nالاسم: ${submittedData?.name || 'غير معروف'}\nالوجهة: ${submittedData?.destination || 'غير محددة'}`)}
              target="_blank" rel="noopener noreferrer"
              className="w-full bg-[#25D366] text-white py-5 rounded-2xl font-black text-xl shadow-2xl flex items-center justify-center gap-4"
            >
              <MessageCircle size={30} /> تأكيد الحجز عبر واتساب
            </a>
            <button onClick={() => setShowSuccessModal(false)} className="mt-6 text-gray-400 font-bold underline cursor-pointer">إغلاق</button>
          </div>
        </div>
      )}
 
      {!selectedPackage && !showSuccessModal && (
        <a 
          href={getWaLink('مرحباً 3M Travel، أرغب في الاستفسار عن العروض')}
          target="_blank" rel="noopener noreferrer"
          className="fixed bottom-10 left-10 z-[500] bg-[#25D366] text-white p-6 rounded-full shadow-2xl hover:scale-110 transition-all animate-fade-in"
        >
          <MessageCircle size={38} />
        </a>
      )}

      {toastData && (
        <div className="fixed bottom-10 right-10 z-[100] bg-white border-r-8 border-[#FFD700] p-6 rounded-3xl shadow-[0_20px_50px_rgba(0,0,0,0.15)] flex items-center gap-5 max-w-sm animate-slide-up border border-gray-100">
          <div className="bg-blue-100 p-4 rounded-2xl text-[#003B73] shrink-0"><Bell size={30} className="animate-pulse" /></div>
          <div>
            <p className="text-lg font-black text-[#003B73] leading-tight mb-1 text-right">{toastData.name} من {toastData.city}</p>
            <p className="text-sm font-bold text-gray-500 text-right">حجز <span className="text-[#003B73] underline">{toastData.pkg}</span> منذ {toastData.time}</p>
          </div>
        </div>
      )}
 
      <nav className={`fixed w-full z-[550] transition-all duration-500 ${isScrolled ? 'bg-white/95 backdrop-blur-xl shadow-xl py-4' : 'bg-gradient-to-b from-black/70 to-transparent py-8'}`}>
        <div className="container mx-auto px-6 flex justify-between items-center">
          <div className="flex items-center gap-4 cursor-pointer" onClick={() => { window.location.hash = ''; setRoute('/'); }}>
            <div className="bg-white p-1 rounded-full shadow-lg overflow-hidden">
              <SafeImage src={CONFIG.logoUrl} alt="3M Logo" className="h-10 w-10 object-contain" />
            </div>
            <span className={`text-2xl md:text-3xl font-black tracking-tighter ${isScrolled ? 'text-[#003B73]' : 'text-white'}`}>3M TRAVEL</span>
          </div>
          <div className={`hidden lg:flex gap-10 font-black text-lg ${isScrolled ? 'text-gray-700' : 'text-white'}`}>
            <a href={route === '/' ? "#destinations" : "#"} onClick={() => { if(route !== '/') { window.location.hash = ''; setTimeout(() => document.getElementById('destinations')?.scrollIntoView(), 100); } }} className="hover:text-[#FFD700] transition-colors">الوجهات</a>
            <a href={route === '/' ? "#packages" : "#"} onClick={() => { if(route !== '/') { window.location.hash = ''; setTimeout(() => document.getElementById('packages')?.scrollIntoView(), 100); } }} className="hover:text-[#FFD700] transition-colors">الباقات</a>
            <a href={route === '/' ? "#booking" : "#"} onClick={() => { if(route !== '/') { window.location.hash = ''; setTimeout(() => document.getElementById('booking')?.scrollIntoView(), 100); } }} className={`px-8 py-3 rounded-2xl shadow-2xl transition-all ${isScrolled ? 'bg-[#003B73] text-white' : 'bg-[#FFD700] text-[#003B73]'}`}>سجل طلبك</a>
          </div>
          <button onClick={() => setMobileMenuOpen(true)} className="lg:hidden p-2 rounded-xl bg-white/10 cursor-pointer"><Menu size={32} className={isScrolled ? 'text-[#003B73]' : 'text-white'} /></button>
        </div>
      </nav>

      {mobileMenuOpen && (
        <div className="fixed inset-0 bg-[#003B73]/98 backdrop-blur-2xl z-[800] flex flex-col items-center justify-center text-white text-center px-10 animate-fade-in">
          <button onClick={() => setMobileMenuOpen(false)} className="absolute top-10 right-10 bg-white/10 p-3 rounded-full cursor-pointer"><X size={38} /></button>
          <div className="flex flex-col gap-10 text-3xl font-black italic">
            <a href="#destinations" onClick={() => setMobileMenuOpen(false)}>الوجهات السياحية</a>
            <a href="#packages" onClick={() => setMobileMenuOpen(false)}>عروض الباقات</a>
            <a href="#booking" onClick={() => setMobileMenuOpen(false)} className="bg-[#FFD700] text-[#003B73] px-12 py-5 rounded-3xl mt-6 shadow-2xl">سجل طلبك الآن</a>
          </div>
        </div>
      )}

      {destData ? (
        <DestinationPage data={destData} goBack={() => { window.location.hash = ''; setRoute('/'); }} onSelectPackage={setSelectedPackage} />
      ) : (
        <main>
          <header className="relative h-screen min-h-[700px] flex items-center justify-center text-center text-white overflow-hidden bg-[#003B73]">
            <div className="absolute inset-0 bg-black/45 z-10" />
            <SafeImage
              priority={true}
              src={TRAVEL_DATA[0].mainImg}
              className="absolute inset-0 w-full h-full object-cover scale-105 animate-slow-zoom"
              alt="3M Travel Hero"
            />
            <div className="relative z-20 container mx-auto px-6 mt-16 max-w-5xl">
              <div className="bg-[#FFD700] text-[#003B73] px-8 py-3 rounded-full font-black text-sm mb-10 inline-flex items-center gap-3 shadow-2xl animate-bounce">
                <Sparkles size={20} /> العروض الحصرية 2026
              </div>
              <h1 className="text-6xl md:text-9xl font-black mb-10 tracking-tighter leading-tight drop-shadow-2xl italic">وجهتك القادمة <br /> <span className="text-[#FFD700]">تبدأ من هنا</span></h1>
              <p className="text-xl md:text-3xl mb-14 max-w-4xl mx-auto font-bold opacity-95 leading-relaxed text-blue-50">نحن بوابتك الحصرية لأرقى فنادق مارينا شرم وأجمل معالم مصر السياحية.</p>
              <div className="flex flex-col sm:flex-row gap-6 justify-center">
                <a href="#packages" className="bg-white text-[#003B73] px-12 py-5 rounded-2xl font-black text-xl shadow-2xl hover:bg-[#FFD700] transition-all">استعرض الباقات</a>
                <a href="#destinations" className="bg-white/10 backdrop-blur-md border-2 border-white/30 text-white px-12 py-5 rounded-2xl font-black text-xl hover:bg-white hover:text-[#003B73] transition-all">اكتشف الوجهات</a>
              </div>
            </div>
          </header>
     
          <section className="py-24 bg-white">
            <div className="container mx-auto px-6 grid grid-cols-1 md:grid-cols-3 gap-12 text-center">
              {[
                { icon: ShieldCheck, title: 'أمان وثقة تامة', desc: 'حجوزات رسمية وتأمين كامل لراحتك.' },
                { icon: Clock,       title: 'خدمة 24/7',  desc: 'فريق دعم فني وسياحي متواجد معك دائماً.' },
                { icon: CreditCard,  title: 'تسعير عادل',   desc: 'أسعار حصرية لا تتوفر على المنصات العالمية.' },
              ].map(({ icon: Icon, title, desc }) => (
                <div key={title} className="bg-gray-50 p-12 rounded-[3rem] border border-gray-100 hover:shadow-2xl transition-all">
                  <div className="bg-white w-20 h-20 rounded-3xl shadow-xl flex items-center justify-center mx-auto mb-8 text-[#003B73]"><Icon size={40} /></div>
                  <h3 className="text-2xl font-black mb-5 text-[#003B73]">{title}</h3>
                  <p className="font-bold text-gray-600 text-lg leading-relaxed">{desc}</p>
                </div>
              ))}
            </div>
          </section>
     
          <section id="destinations" className="py-28 bg-gray-50 border-y border-gray-100">
            <div className="container mx-auto px-6 text-center">
              <h2 className="text-5xl md:text-6xl font-black mb-20 text-[#003B73] tracking-tighter italic">اكتشف وجهاتنا</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10">
                {TRAVEL_DATA.map((dest) => (
                  <div key={dest.id} className="group relative h-[500px] rounded-[3rem] overflow-hidden shadow-2xl hover:shadow-[0_30px_60px_rgba(0,59,115,0.25)] transition-all bg-gray-200">
                    <SafeImage src={dest.mainImg} className="absolute inset-0 w-full h-full object-cover group-hover:scale-110 transition-transform duration-1000 z-10" alt={dest.name} />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#003B73] via-[#003B73]/30 to-transparent z-20 opacity-90" />
                    <div className="absolute bottom-0 p-10 text-white w-full text-right z-30">
                      <h3 className="text-5xl font-black mb-6 drop-shadow-2xl italic tracking-tighter">{dest.name}</h3>
                      <a href={`#/${dest.id}`} className="inline-flex bg-[#FFD700] text-[#003B73] px-8 py-4 rounded-2xl font-black text-base shadow-xl active:scale-95">استكشف العروض <ArrowLeft size={20} /></a>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>
     
          <PackagesSection onSelectPackage={setSelectedPackage} />
     
          <section id="reviews" className="py-28 bg-[#003B73] text-white">
            <div className="container mx-auto px-6 text-center">
              <h2 className="text-5xl md:text-6xl font-black mb-20 tracking-tighter italic">لماذا يختارنا العملاء؟</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-10 max-w-7xl mx-auto text-right">
                {REVIEWS_DATA.map((rev) => (
                  <div key={rev.id} className="bg-white/5 backdrop-blur-xl p-10 rounded-[3rem] border border-white/10 relative hover:bg-white/10 transition-all shadow-2xl group text-right">
                    <Quote className="absolute top-10 left-10 text-white/5 group-hover:text-[#FFD700]/10 transition-colors" size={80} />
                    <div className="flex items-center gap-1.5 mb-8">
                      {Array.from({ length: rev.rating }).map((_, i) => (<Star key={i} size={22} className="fill-[#FFD700] text-[#FFD700]" />))}
                    </div>
                    <p className="text-xl text-blue-50 font-bold mb-10 italic leading-relaxed">"{rev.comment}"</p>
                    <div className="flex justify-between items-center border-t border-white/10 pt-8">
                      <span className="font-black text-xl text-white">{rev.user}</span>
                      <span className="bg-[#FFD700] text-[#003B73] px-4 py-1.5 rounded-lg text-xs font-black uppercase shadow-md">{rev.result}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>
     
          <section id="booking" className="py-32 bg-white">
            <div className="container mx-auto px-6 max-w-5xl">
              <div className="bg-gray-50 rounded-[4rem] shadow-3xl p-10 md:p-20 border border-gray-100">
                <div className="text-center mb-16">
                  <h2 className="text-4xl md:text-6xl font-black mb-6 text-[#003B73] tracking-tighter">صمم إجازتك الخاصة</h2>
                  <p className="text-gray-500 font-bold text-xl italic">أخبرنا بتطلعاتك وسنقوم بصناعة برنامج مخصص لك.</p>
                </div>
                <form className="space-y-8" onSubmit={handleFormSubmit}>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                    <input required name="name" value={form.name} onChange={handleFormChange} type="text" placeholder="الاسم بالكامل" className="w-full bg-white border border-gray-200 p-5 rounded-2xl outline-none focus:border-[#003B73] font-bold text-lg shadow-sm" />
                    <input required name="phone" value={form.phone} onChange={handleFormChange} type="tel" placeholder="رقم الجوال" className="w-full bg-white border border-gray-200 p-5 rounded-2xl outline-none focus:border-[#003B73] font-bold text-lg shadow-sm" />
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                    <select required name="destination" value={form.destination} onChange={handleFormChange} className="w-full bg-white border border-gray-200 p-5 rounded-2xl outline-none focus:border-[#003B73] font-black text-lg text-gray-700 cursor-pointer">
                      <option value="">-- اختر المدينة --</option>
                      {TRAVEL_DATA.map(d => <option key={d.id} value={d.name}>{d.name}</option>)}
                    </select>
                    <input required name="date" value={form.date} onChange={handleFormChange} type="date" className="w-full bg-white border border-gray-200 p-5 rounded-2xl outline-none focus:border-[#003B73] font-black text-lg cursor-pointer" />
                  </div>
                  <button type="submit" className="w-full bg-[#003B73] text-white py-6 rounded-3xl font-black text-2xl shadow-2xl hover:bg-[#FFD700] hover:text-[#003B73] transition-all flex items-center justify-center gap-4 group cursor-pointer active:scale-95">
                    إرسال الطلب الآن <Send size={28} className="group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
                  </button>
                </form>
              </div>
            </div>
          </section>
        </main>
      )}
 
      <footer className="bg-gray-950 text-gray-400 py-28 border-t border-white/5 mt-auto">
        <div className="container mx-auto px-6 grid grid-cols-1 md:grid-cols-3 gap-20 text-center md:text-right">
          <div>
            <div className="bg-white inline-block p-2.5 rounded-full mb-8 shadow-2xl">
              <SafeImage src={CONFIG.logoUrl} alt="3M Footer Logo" className="h-20 w-20 object-contain rounded-full" />
            </div>
            <div className="text-5xl font-black text-white mb-8 tracking-tighter italic">3M TRAVEL</div>
            <p className="text-gray-400 leading-relaxed text-lg font-bold max-w-sm mx-auto md:mx-0">الوكالة السياحية الأولى في مصر لتقديم حلول إقامة فاخرة وتجارب ترفيهية استثنائية.</p>
          </div>
          <div className="space-y-10">
            <h4 className="text-[#FFD700] font-black text-2xl border-r-4 border-[#FFD700] pr-4 inline-block">تواصل معنا</h4>
            <div className="space-y-6 text-lg font-black">
              <div className="flex items-center justify-center md:justify-start gap-4"><MapPin size={24} className="text-[#FFD700]" /> <span>{CONFIG.address}</span></div>
              <div className="flex items-center justify-center md:justify-start gap-4"><Phone size={24} className="text-[#FFD700]" /> <span dir="ltr">{CONFIG.phoneDisplay}</span></div>
            </div>
          </div>
          <div className="space-y-10">
            <h4 className="text-[#FFD700] font-black text-2xl border-r-4 border-[#FFD700] pr-4 inline-block">تفاعل معنا</h4>
            <div className="flex justify-center md:justify-start gap-6">
              <a href={CONFIG.fbLink} target="_blank" rel="noopener noreferrer" className="bg-white/5 p-5 rounded-2xl hover:bg-[#FFD700] transition-all shadow-xl"><Facebook size={32} /></a>
              <a href="#" className="bg-white/5 p-5 rounded-2xl hover:bg-[#FFD700] transition-all shadow-xl"><Instagram size={32} /></a>
            </div>
          </div>
        </div>
        <div className="text-center text-xs text-gray-700 mt-28 pt-10 border-t border-white/5 font-black uppercase tracking-[0.3em]">© {new Date().getFullYear()} 3M TRAVEL Agency</div>
      </footer>
 
      <style>{`
        @keyframes fadeIn    { from { opacity:0; transform:translateY(20px) } to { opacity:1; transform:translateY(0) } }
        @keyframes slideUp   { from { opacity:0; transform:translateY(80px) scale(0.95) } to { opacity:1; transform:translateY(0) scale(1) } }
        @keyframes slow-zoom { from { transform:scale(1) } to { transform:scale(1.1) } }
        .animate-fade-in   { animation: fadeIn  0.8s cubic-bezier(.16,1,.3,1) forwards }
        .animate-slide-up  { animation: slideUp 0.7s cubic-bezier(.16,1,.3,1) forwards }
        .animate-slow-zoom { animation: slow-zoom 30s infinite alternate linear }
        html { scroll-behavior: smooth; font-display: swap; }
        .hide-scrollbar::-webkit-scrollbar { display: none; }
        .hide-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }
        ::-webkit-scrollbar       { width: 10px }
        ::-webkit-scrollbar-track { background: #f1f5f9 }
        ::-webkit-scrollbar-thumb { background: #003B73; border-radius: 10px; }
      `}</style>
    </div>
  );
}